import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSectionHeader,
  ContextMenuSeparator,
  CONTEXT_MENU_PANEL_STYLE,
} from '../ui/context-menu';
import { GOLD, TEXT_BODY, TEXT_MUTED } from '../../lib/medievalTheme';
import { useEscDismiss } from './useEscDismiss';
import { DICE_CONFIGS } from '../../utils/game/diceMesh';
import { describeBinding, getBinding } from '../../utils/game/keybindings';
import { cn } from '../../lib/utils';
import { k, t } from '../../localization/t';

// Hover sub-panels (status effects, scry direction) are rendered inline
// next to the parent menu row, not via a portal. They need to track
// the row's position. Re-uses the same visual tokens as the portal'd
// menu so the family reads as a single component.
const FLYOUT_PANEL_STYLE = {
  ...CONTEXT_MENU_PANEL_STYLE,
  boxShadow: '0 18px 48px rgba(0,0,0,0.55), 0 0 20px rgba(180,140,60,0.06)',
};

// Sits above the in-game UI strip (z-1000) and modal-like overlays
// (z-1050) but below blocking dialogs (z-1200+) so a dialog reliably
// covers an open context menu.
const MENU_Z = 1100;

// Breathing room kept between a flyout and the viewport edge. Mirrors the
// portal'd menu's VIEWPORT_PADDING so the family clamps consistently.
const FLYOUT_VIEWPORT_PADDING = 12;

// Places a hover flyout beside its trigger row while keeping it fully
// on-screen. Prefers opening to the right and flips left only when the right
// edge can't fit the panel (unchanged). The part the bare `top: 0` anchor
// missed: a row low on the screen grows the panel straight off the bottom,
// so we pull it up by exactly the overshoot and then refuse to climb above
// the top padding (a panel taller than the viewport keeps its head visible
// and clips at the bottom instead). Measured in useLayoutEffect. Before the
// browser paints. So the panel never flashes at the unclamped position.
// `visible` re-measures each time the flyout appears; pass a constant `true`
// for flyouts that only mount while shown.
function useFlyoutPlacement(triggerRef, flyoutRef, visible) {
  const [placement, setPlacement] = useState({ side: 'right', top: 0 });

  useLayoutEffect(() => {
    if (!visible) return;
    const trigger = triggerRef.current;
    const flyout = flyoutRef.current;
    if (!trigger || !flyout) return;

    const rect = trigger.getBoundingClientRect();
    const flyoutWidth = flyout.offsetWidth || 180;
    const flyoutHeight = flyout.offsetHeight;

    const side = window.innerWidth - rect.right >= flyoutWidth + 8 ? 'right' : 'left';

    const overshoot = (rect.top + flyoutHeight) - (window.innerHeight - FLYOUT_VIEWPORT_PADDING);
    let top = overshoot > 0 ? -overshoot : 0;
    if (rect.top + top < FLYOUT_VIEWPORT_PADDING) top = FLYOUT_VIEWPORT_PADDING - rect.top;

    setPlacement({ side, top });
  }, [visible]);

  return placement;
}

// Turn a {side, top} placement into the absolute-position style. The 4px
// pad on the open side is the hit-detectable bridge that lets the pointer
// cross from the trigger row into the flyout without it collapsing.
function flyoutPosStyle({ side, top }) {
  return side === 'right'
    ? { left: '100%', top, paddingLeft: 4 }
    : { right: '100%', top, paddingRight: 4 };
}

// A hover-expandable submenu row. Renders a trigger styled like a normal
// ContextMenuItem with a ▶ affordance; hovering (or clicking) reveals a
// flyout panel of `children` to the row's right, flipping left when the
// viewport edge is close. The grace-timer open/close lets the pointer
// cross the visual gap between the trigger and the flyout without the
// panel collapsing. Nests arbitrarily deep. Each level is its own
// `relative` container, so child flyouts cascade outward from `left:100%`.
function SubmenuItem({ label, children, panelClassName = 'min-w-[8cqw]' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimerRef = useRef(null);
  const placement = useFlyoutPlacement(triggerRef, flyoutRef, open);

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <ContextMenuItem onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
        {label}
        <span className="ml-auto text-[0.625cqw]" style={{ color: TEXT_MUTED }}>▶</span>
      </ContextMenuItem>
      {open && (
        <div
          className="absolute z-10"
          style={flyoutPosStyle(placement)}
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <div ref={flyoutRef} className={cn(panelClassName, 'p-1 text-sm')} style={FLYOUT_PANEL_STYLE}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

// "Send to ▶ …." The single destination hub shared by the board-card
// and hand-card menus. `directItems` are one-click destinations (Hand,
// Cemetery) rendered first; Spellbook/Atlas nest one level deeper into
// Top/Bottom/Shuffle. `sendFn(card, pileName, shouldShuffle, position)`
// matches both actions.sendCardToPile and actions.sendHandCardToPile.
function SendToPileSubmenu({ cardInstance, sendFn, directItems = [] }) {
  return (
    <SubmenuItem label={t('Send to')}>
      {directItems.map((item) => (
        <ContextMenuItem key={item.label} onClick={item.onClick}>
          {item.label}
        </ContextMenuItem>
      ))}
      {directItems.length > 0 && <ContextMenuSeparator />}
      {[k('Spellbook'), k('Atlas')].map((pileName) => (
        <SubmenuItem key={pileName} label={t(pileName)}>
          <ContextMenuItem onClick={() => sendFn(cardInstance, pileName, false, 'top')}>
            {t('Top')}
          </ContextMenuItem>
          <ContextMenuItem onClick={() => sendFn(cardInstance, pileName, false, 'bottom')}>
            {t('Bottom')}
          </ContextMenuItem>
          <ContextMenuItem onClick={() => sendFn(cardInstance, pileName, true)}>
            {t('Shuffle in')}
          </ContextMenuItem>
        </SubmenuItem>
      ))}
    </SubmenuItem>
  );
}

/**
 * Sandbox-mode context menu. Variants:
 *   - card menu (tap, flip, send to hand, send to pile, banish)
 *   - pile menu (draw, shuffle, search, scry, draw to hand)
 *   - pile card menu (take to hand, spawn on field, banish. For cards
 *     inside an opened pile-content dialog)
 *   - hand card menu (put back into spellbook / atlas / cemetery)
 *   - token menu (delete)
 *   - dice menu (roll, set value, delete)
 *
 * Built on the shared shadcn-style ContextMenuContent so the visual
 * language matches every other right-click menu in the app. Opaque
 * dark surface, gold border, blurred backdrop, gold-tinted hover.
 */
export default function GameContextMenu({ contextMenu, actions, viewScale = 1, onClose }) {
  useEscDismiss(!!contextMenu, onClose);
  if (!contextMenu) return null;

  const onOpenChange = (open) => { if (!open) onClose?.(); };
  const surfaceProps = {
    open: true,
    position: { x: contextMenu.x, y: contextMenu.y },
    positioning: 'flip',
    viewScale,
    ornaments: true,
    zIndex: MENU_Z,
    onOpenChange,
  };

  if (contextMenu.type === 'card') {
    return <CardMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'pile') {
    return <PileMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'pileScry') {
    return <PileScryMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'pileCard') {
    return <PileCardMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'handcard') {
    return <HandCardMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'token') {
    return <TokenMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  if (contextMenu.type === 'dice') {
    return <DiceMenu surface={surfaceProps} contextMenu={contextMenu} actions={actions} />;
  }
  return null;
}

function CardMenu({ surface, contextMenu, actions }) {
  const {
    cardInstance, mesh,
    selectionSize = 0,
    groupId = null,
    selectionAlreadyGrouped = false,
    isLocallyOwned = true,
    isMultiplayer = false,
    canEquip = false,
  } = contextMenu;

  // Defend against a stale or partially-populated payload. A missing
  // cardInstance would crash render and leave the user with no menu at
  // all. Falling back to a neutral label keeps the action items usable.
  if (!cardInstance) return null;
  const cardName = cardInstance.name || t('card');

  const canGroup = !selectionAlreadyGrouped && (selectionSize >= 2 || (selectionSize >= 1 && !groupId));
  const canUngroup = !!groupId;

  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Card actions for {name}', { name: cardName })}
      className="min-w-48 text-sm"
    >
      <ContextMenuLabel>{cardName}</ContextMenuLabel>
      <ContextMenuItem onClick={() => actions.tapCard(cardInstance, mesh)}>
        {cardInstance.tapped ? t('Untap') : t('Tap')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.flipCard(cardInstance, mesh)}>
        {t('Flip')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.switchCardOwnership(cardInstance, mesh)}>
        {isMultiplayer
          ? (isLocallyOwned ? t('Give to opponent') : t('Take control'))
          : t('Switch ownership')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.duplicateCard(cardInstance)}>
        {t('Duplicate')}
      </ContextMenuItem>

      {canEquip && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => actions.openEquipModal(cardInstance)}>
            {t('Equip to…')}
          </ContextMenuItem>
        </>
      )}

      {/* Imposter. Manual mask removal. The damage trigger covers the
          codex case ("until damaged"), but players also want a tidy way
          to step out of the mask without taking a hit. The entry only
          appears when an _originalIdentity snapshot is present (i.e. a
          mask is actually on). */}
      {isLocallyOwned && cardInstance._isImposter && cardInstance._originalIdentity && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => actions.takeOffMask(cardInstance)}>
            {t('Take off mask')}
          </ContextMenuItem>
        </>
      )}

      {/* No combat or automation intents here. Move / attack are board
          gestures (drag), and Attack / Shoot plus every activated ability
          live in the double-click action menu. The right-click menu is
          for card-state housekeeping only. */}

      {(canGroup || canUngroup) && <ContextMenuSeparator />}
      {canGroup && (
        <ContextMenuItem onClick={() => actions.groupSelected(cardInstance)}>
          {selectionSize >= 2 ? t('Group {n} selected', { n: selectionSize }) : t('Group with selection')}
        </ContextMenuItem>
      )}
      {canUngroup && (
        <ContextMenuItem onClick={() => actions.ungroup(cardInstance)}>
          {t('Ungroup')}
        </ContextMenuItem>
      )}

      <ContextMenuSeparator />
      <SendToPileSubmenu
        cardInstance={cardInstance}
        sendFn={actions.sendCardToPile}
        directItems={[
          { label: t('Hand'), onClick: () => actions.sendToHand(cardInstance) },
          {
            label: t('Cemetery'),
            // Single cemetery sink. SendCardToPile owns the site→Rubble
            // routing (Sorcery TCG rules), token banish-redirect, and
            // Deathrite firing for EVERY send path (hotkey, drag, menu).
            onClick: () => actions.sendCardToPile(cardInstance, 'Cemetery'),
          },
        ]}
      />

      <ContextMenuSeparator />
      <ContextMenuItem tone="danger" onClick={() => actions.banishCard(cardInstance)}>
        {t('Banish')}
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

function PileMenu({ surface, contextMenu, actions }) {
  const pile = contextMenu.pile;
  const [showScry, setShowScry] = useState(false);
  const scryTriggerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const canScry = !pile.infinite && pile.cards.length > 0;
  // v1.66. Draw items only appear in sandbox / offline. In real
  // matches, drawing must go through the engine (Start Phase prompt
  // or card-effect draws).
  const canFreeformDraw = actions?.canFreeformDraw?.() !== false;

  // Cancel any pending close from crossing the gap between the trigger
  // row and the flyout. Mouse-leaves schedule a close with a short
  // grace window; mouse-enters on either the trigger or the flyout
  // cancel that timer so the flyout stays open while the pointer is
  // actually over either half.
  const openScryMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setShowScry(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setShowScry(false), 150);
  };
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('{name} pile actions', { name: pile.name })}
      className="min-w-48 text-sm"
    >
      <ContextMenuLabel>{t('{name} ({n} cards)', { name: pile.name, n: pile.cards.length })}</ContextMenuLabel>
      {canFreeformDraw && (
        <ContextMenuItem onClick={() => actions.drawCard(pile.id)}>{t('Draw card')}</ContextMenuItem>
      )}
      <ContextMenuItem onClick={() => actions.shufflePile(pile)}>{t('Shuffle')}</ContextMenuItem>
      <ContextMenuItem onClick={() => actions.openPileSearch(pile)}>{t('Search')}</ContextMenuItem>
      {canScry && (
        <div
          ref={scryTriggerRef}
          className="relative"
          onMouseEnter={openScryMenu}
          onMouseLeave={scheduleClose}
        >
          <ContextMenuItem onClick={(e) => { e.stopPropagation(); setShowScry(!showScry); }}>
            {t('Scry')}
            <span className="ml-auto text-[0.625cqw]" style={{ color: TEXT_MUTED }}>▶</span>
          </ContextMenuItem>
          {showScry && (
            <ScryFlyout
              pile={pile}
              actions={actions}
              parentRef={scryTriggerRef}
              onMouseEnter={openScryMenu}
              onMouseLeave={scheduleClose}
            />
          )}
        </div>
      )}
      {canFreeformDraw && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => actions.drawPileToHand(pile)}>{t('Draw to hand')}</ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}

function PileScryMenu({ surface, contextMenu, actions }) {
  const pile = contextMenu.pile;
  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Scry direction')}
      className="min-w-[10cqw] text-sm"
    >
      <ContextMenuSectionHeader>{t('Scry {name}', { name: pile.name })}</ContextMenuSectionHeader>
      <ContextMenuItem onClick={() => actions.openScry(pile, 'top')}>{t('From top')}</ContextMenuItem>
      <ContextMenuItem onClick={() => actions.openScry(pile, 'bottom')}>{t('From bottom')}</ContextMenuItem>
    </ContextMenuContent>
  );
}

// Right-click menu for a card inside an opened pile-content dialog.
// Every action carries the source pile so ownership routes through
// it. Banishing from "Cemetery P1" always lands in "Banished P1".
// Banish is hidden for infinite (token) piles and for Banished
// sources, where the action would be a no-op.
function PileCardMenu({ surface, contextMenu, actions }) {
  const { cardInstance, pile } = contextMenu;
  const isInfinite = !!pile?.infinite;
  const isBanishedSource = !!pile?.name?.startsWith('Banished');

  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Card actions for {name}', { name: cardInstance.name })}
      className="min-w-48 text-sm"
    >
      <ContextMenuLabel>{cardInstance.name}</ContextMenuLabel>
      <ContextMenuItem onClick={() => actions.pileCardTakeToHand(pile, cardInstance)}>
        {t('Take to hand')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.pileCardSpawnOnField(pile, cardInstance)}>
        {t('Place on field…')}
      </ContextMenuItem>
      {!isInfinite && !isBanishedSource && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem tone="danger" onClick={() => actions.pileCardBanish(pile, cardInstance)}>
            {t('Banish')}
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}

function ScryFlyout({ pile, actions, parentRef, onMouseEnter, onMouseLeave }) {
  const flyoutRef = useRef(null);
  // Mounted only while shown, so it measures once on mount (visible=true).
  const placement = useFlyoutPlacement(parentRef, flyoutRef, true);

  return (
    <div
      className="absolute z-10"
      style={flyoutPosStyle(placement)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div ref={flyoutRef} className="min-w-[10cqw] p-1 text-sm" style={FLYOUT_PANEL_STYLE}>
        <ContextMenuItem onClick={() => actions.openScry(pile, 'top')}>{t('From top')}</ContextMenuItem>
        <ContextMenuItem onClick={() => actions.openScry(pile, 'bottom')}>{t('From bottom')}</ContextMenuItem>
      </div>
    </div>
  );
}

function HandCardMenu({ surface, contextMenu, actions }) {
  const { cardInstance } = contextMenu;
  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Hand card actions')}
      className="min-w-48 text-sm"
    >
      <ContextMenuLabel>
        {cardInstance.faceDown ? t('Face-down card') : cardInstance.name}
      </ContextMenuLabel>
      <ContextMenuItem onClick={() => actions.revealEntireHand(cardInstance)}>
        {t('Show hand to opponent')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.toggleHandCardReveal(cardInstance)}>
        {cardInstance.revealed ? t('Hide from opponent') : t('Show to opponent')}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => actions.toggleHandCardFaceDown(cardInstance)}>
        {cardInstance.faceDown ? t('Flip face up') : t('Flip face down (for hidden play)')}
      </ContextMenuItem>
      <ContextMenuSeparator />
      <SendToPileSubmenu
        cardInstance={cardInstance}
        sendFn={actions.sendHandCardToPile}
        directItems={[
          { label: t('Cemetery'), onClick: () => actions.sendHandCardToPile(cardInstance, 'Cemetery', false) },
        ]}
      />
    </ContextMenuContent>
  );
}

function TokenMenu({ surface, contextMenu, actions }) {
  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Token actions')}
      className="min-w-48 text-sm"
    >
      <ContextMenuLabel>{t('Token')}</ContextMenuLabel>
      <ContextMenuItem tone="danger" onClick={() => actions.deleteToken(contextMenu.tokenInstance)}>
        {t('Delete')}
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

// Face chips per row, so every body lays out as a tidy block: a d20 reads as
// four rows of five, a d12 as two of six, a d4 as one row.
const FACE_COLUMNS = { 4: 4, 6: 6, 8: 4, 10: 5, 12: 6, 20: 5 };

const KEY_STYLE = {
  display: 'inline-block',
  minWidth: '1.25em',
  padding: '0.05em 0.4em',
  background: 'linear-gradient(180deg, rgba(40,34,24,0.9) 0%, rgba(20,17,12,0.95) 100%)',
  border: '1px solid rgba(180,140,60,0.35)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.7em',
  fontFamily: 'ui-monospace, "SF Mono", "Cascadia Mono", monospace',
  fontWeight: 700,
  lineHeight: 1.5,
  textAlign: 'center',
  color: '#d4a843',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
};

const FACE_REST = {
  color: TEXT_BODY,
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(180,140,60,0.14)',
  boxShadow: 'none',
};
const FACE_HOVER = {
  color: '#e8d5a0',
  background: 'rgba(180,140,60,0.14)',
  border: '1px solid rgba(180,140,60,0.35)',
  boxShadow: 'none',
};
const FACE_ACTIVE = {
  color: '#fff3d6',
  background: `linear-gradient(180deg, ${GOLD} 0.36) 0%, ${GOLD} 0.22) 100%)`,
  border: `1px solid ${GOLD} 0.6)`,
  boxShadow: `0 0 10px ${GOLD} 0.3), inset 0 1px 0 rgba(255,255,255,0.12)`,
};
function Key({ children }) {
  return <span style={KEY_STYLE}>{children}</span>;
}

function DieGlyph() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 2.5 20.5 7.25v9.5L12 21.5 3.5 16.75v-9.5z" />
      <path d="M12 2.5 7 12l5 9.5 5-9.5z" />
      <path d="M3.5 7.25 7 12M20.5 7.25 17 12M3.5 16.75 7 12M20.5 16.75 17 12" />
    </svg>
  );
}

function RollGlyph() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v5h-5" />
    </svg>
  );
}

const paint = (el, look) => Object.assign(el.style, look);

// The die's own menu: what it is and what it shows, Roll as the first row, a
// block of face chips to turn it by hand, and the quiet way to take it away.
// Each row carries its key, the same bindings the hovered die answers to.
function DiceMenu({ surface, contextMenu, actions }) {
  const { diceInstance } = contextMenu;
  const faces = DICE_CONFIGS[diceInstance.dieType]?.faces || 6;
  const cols = FACE_COLUMNS[faces] || 6;
  const rollKey = describeBinding(getBinding('pile.shuffle'));
  const removeKey = describeBinding(getBinding('card.toCemetery'));

  return (
    <ContextMenuContent
      {...surface}
      ariaLabel={t('Die actions')}
      className="text-sm"
    >
      <div className="flex items-center justify-between gap-3 px-3 pt-1.5 pb-1 select-none">
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#e8d5a0' }}>
          <DieGlyph />
          <span>{diceInstance.dieType}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-[0.6em] font-medium" style={{ color: TEXT_MUTED }}>{t('showing')}</span>
          <span className="text-base font-bold leading-none" style={{ color: '#d4a843', textShadow: `0 0 12px ${GOLD} 0.45)` }}>{diceInstance.value}</span>
        </div>
      </div>

      <ContextMenuItem className="justify-between" onClick={() => actions.rollDice(diceInstance)}>
        <span className="flex items-center gap-1.5"><RollGlyph />{t('Roll')}</span>
        {rollKey ? <Key>{rollKey}</Key> : null}
      </ContextMenuItem>

      <div className="flex items-center justify-between pr-3">
        <ContextMenuSectionHeader className="mt-1">{t('Set face')}</ContextMenuSectionHeader>
        {faces >= 2 ? <Key>1–{Math.min(9, faces)}</Key> : null}
      </div>
      <div className="grid gap-1 px-2 pb-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {Array.from({ length: faces }, (_, i) => i + 1).map((v) => {
          const active = v === diceInstance.value;
          return (
            <button
              key={v}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className="size-8 rounded-md text-xs font-semibold tabular-nums flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-[1.06] active:scale-95"
              style={active ? FACE_ACTIVE : FACE_REST}
              onMouseEnter={(e) => { if (!active) paint(e.currentTarget, FACE_HOVER); }}
              onMouseLeave={(e) => { if (!active) paint(e.currentTarget, FACE_REST); }}
              onClick={() => actions.setDiceValue(diceInstance, v)}
            >
              {v}
            </button>
          );
        })}
      </div>

      <ContextMenuSeparator />
      <ContextMenuItem tone="danger" className="justify-between" onClick={() => actions.deleteDice(diceInstance)}>
        <span>{t('Remove')}</span>
        {removeKey ? <Key>{removeKey}</Key> : null}
      </ContextMenuItem>
    </ContextMenuContent>
  );
}
